import { useFormik } from 'formik';
import React, { useState } from 'react';
import { FiLock, FiMail } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { Button } from '@design-system/components/atoms/Button';
import { FormField } from '@design-system/components/molecules/FormField';
import { useLogin } from '@features/authentication/hooks/useAuth';
import type { LoginCredentials } from '@features/authentication/types';
import { APP_ROUTES } from '@shared/constants/routes.constants';
import { loginSchema } from '@shared/utils/validators/yup-schemas';

export interface LoginFormProps {
  /**
   * Callback when login is successful
   */
  onSuccess?: () => void;

  /**
   * Callback when login fails
   */
  onError?: (error: Error) => void;
}

/**
 * LoginForm organism
 * Complete login form with email, password, and validation
 */
export const LoginForm: React.FC<LoginFormProps> = ({ onSuccess, onError }) => {
  const [serverError, setServerError] = useState<string>('');
  const loginMutation = useLogin();

  const formik = useFormik<LoginCredentials>({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: loginSchema,
    onSubmit: async (values) => {
      setServerError('');

      try {
        await loginMutation.mutateAsync(values);
        onSuccess?.();
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : 'Erro ao fazer login. Tente novamente.';

        setServerError(errorMessage);
        onError?.(error instanceof Error ? error : new Error(errorMessage));
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit} className="flex flex-col gap-4">
      <FormField
        name="email"
        label="Email"
        type="email"
        placeholder="seu@email.com"
        value={formik.values.email}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.email}
        touched={formik.touched.email}
        required
        fullWidth
        autoComplete="email"
        icon={<FiMail className="w-5 h-5" />}
      />

      <FormField
        name="password"
        label="Senha"
        type="password"
        placeholder="Digite sua senha"
        value={formik.values.password}
        onChange={formik.handleChange}
        onBlur={formik.handleBlur}
        error={formik.errors.password}
        touched={formik.touched.password}
        required
        fullWidth
        autoComplete="current-password"
        icon={<FiLock className="w-5 h-5" />}
      />

      <div className="flex">
        <Link
          to={APP_ROUTES.FORGOT_PASSWORD}
          className="text-sm text-purple-600 hover:text-purple-700 hover:underline transition-colors"
        >
          Esqueceu sua senha?
        </Link>
      </div>

      {serverError && (
        <div
          className="p-3 rounded-lg bg-danger-50 border border-danger-200 text-danger-700 text-sm"
          role="alert"
        >
          {serverError}
        </div>
      )}

      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        loading={loginMutation.isPending}
        disabled={loginMutation.isPending || !formik.isValid}
      >
        {loginMutation.isPending ? 'Entrando...' : 'Entrar no Painel'}
      </Button>
    </form>
  );
};
